import React, { useEffect, useState, useRef } from 'react';
import { useAnimatedProgress } from '../../../shared/hooks/useAnimatedProgress';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Spinner,
} from '../../../shared/components/ui';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { useTenantPreferences } from '../../../shared/hooks/useTenantPreferences';
import { EstimateTreeEditor } from './EstimateTreeEditor';
import { EstimateDocumentsPanel } from './EstimateDocumentsPanel';

const STEPS = {
  DESCRIBE: 'describe',
  UPLOAD_DOCS: 'upload_docs',
  EDIT_TEMPLATE: 'edit_template',
  AI_ESTIMATING: 'ai_estimating',
  EDIT: 'edit',
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// Generate temporary IDs for new items
const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Circular Progress Component
function CircularProgress({ percent, size = 120, strokeWidth = 8 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#2563eb"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-200 ease-linear"
        />
      </svg>
      {/* Percentage text in center */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{percent.toFixed(2)}%</span>
      </div>
    </div>
  );
}

// AI Estimation Progress Component
function AIEstimationProgress({ progress, displayProgress, onSkip }) {
  const { total, completed, currentCategory } = progress;

  return (
    <Card>
      <CardContent className="py-12">
        <div className="flex flex-col items-center text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Creating Your Estimate with AI
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md">
            We're analyzing similar projects and generating cost estimates for each line item.
          </p>

          {/* Circular Progress */}
          <div className="mb-6">
            <CircularProgress percent={displayProgress} size={140} strokeWidth={10} />
          </div>

          {/* Current Category */}
          {currentCategory && (
            <div className="mb-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">Currently estimating</span>
              <p className="text-lg font-medium text-blue-600 dark:text-blue-400">{currentCategory}</p>
            </div>
          )}

          {/* Item Count */}
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-8">
            {completed} of {total} items estimated
          </p>

          {/* Skip Button */}
          <Button variant="outline" onClick={onSkip}>
            Skip and Edit Manually
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Simple Line Item Row Component for template editing
function SimpleLineItemRow({ item, onUpdate, onDelete }) {
  const [isEditing, setIsEditing] = useState(item.description === 'New Line Item');
  const [editDescription, setEditDescription] = useState(item.description);

  const handleSave = () => {
    if (editDescription.trim()) {
      onUpdate({ ...item, description: editDescription.trim() });
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditDescription(item.description);
      setIsEditing(false);
    }
  };

  return (
    <tr className="border-t border-gray-100 dark:border-gray-800">
      <td className="py-2 px-3">
        {isEditing ? (
          <input
            type="text"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder="Enter line item description..."
            className="w-full border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        ) : (
          <span
            className="cursor-pointer hover:text-blue-600"
            onClick={() => setIsEditing(true)}
          >
            {item.description}
          </span>
        )}
      </td>
      <td className="py-2 px-3 text-right">
        <button
          onClick={onDelete}
          className="text-red-600 dark:text-red-400 hover:text-red-800 text-xs font-medium"
        >
          Remove
        </button>
      </td>
    </tr>
  );
}

// Simple Category Editor Component for template editing
function SimpleCategoryEditor({ category, onUpdate, onDelete }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditingName, setIsEditingName] = useState(category.categoryName === 'New Category');
  const [editName, setEditName] = useState(category.categoryName);

  const handleSaveName = () => {
    if (editName.trim()) {
      onUpdate({ ...category, categoryName: editName.trim() });
      setIsEditingName(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      setEditName(category.categoryName);
      setIsEditingName(false);
    }
  };

  const handleAddLineItem = () => {
    const newItem = {
      lineItemId: generateTempId(),
      description: 'New Line Item',
      quantity: 1,
      unit: 'LS',
      unitCost: 0,
      totalCost: 0,
      costSource: 'template',
    };
    onUpdate({
      ...category,
      lineItems: [...(category.lineItems || []), newItem],
    });
  };

  const handleUpdateLineItem = (updatedItem) => {
    onUpdate({
      ...category,
      lineItems: category.lineItems.map(item =>
        item.lineItemId === updatedItem.lineItemId ? updatedItem : item
      ),
    });
  };

  const handleDeleteLineItem = (lineItemId) => {
    onUpdate({
      ...category,
      lineItems: category.lineItems.filter(item => item.lineItemId !== lineItemId),
    });
  };

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg mb-4">
      <div className="bg-gray-50 dark:bg-gray-900 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-100"
          >
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {isEditingName ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={handleKeyDown}
              placeholder="Category name..."
              className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          ) : (
            <span
              className="font-semibold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-blue-600"
              onClick={() => setIsEditingName(true)}
            >
              {category.categoryName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {category.lineItems?.length || 0} item{(category.lineItems?.length || 0) !== 1 ? 's' : ''}
          </span>
          <button
            onClick={onDelete}
            className="text-red-600 dark:text-red-400 hover:text-red-800 text-sm font-medium"
          >
            Delete Category
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4">
          {(category.lineItems?.length || 0) === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">No line items. Add at least one line item.</p>
          ) : (
            <table className="w-full text-sm mb-3">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Description</th>
                  <th className="text-right py-2 px-3 w-24 font-medium text-gray-700 dark:text-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(category.lineItems || []).map(item => (
                  <SimpleLineItemRow
                    key={item.lineItemId}
                    item={item}
                    onUpdate={handleUpdateLineItem}
                    onDelete={() => handleDeleteLineItem(item.lineItemId)}
                  />
                ))}
              </tbody>
            </table>
          )}
          <Button variant="outline" size="sm" onClick={handleAddLineItem}>
            + Add Line Item
          </Button>
        </div>
      )}
    </div>
  );
}

// Template Editor Component - allows adding/removing categories and line items
function TemplateEditor({
  estimate,
  onEstimateChange,
  onProceed,
  onBack,
  onSkipAI,
  documentsProcessingCount = 0,
  documentsUploadingCount = 0,
}) {
  // Count total line items for validation
  const totalLineItems = (estimate.categories || []).reduce(
    (sum, cat) => sum + (cat.lineItems?.length || 0), 0
  );

  const documentsBusy = documentsProcessingCount > 0 || documentsUploadingCount > 0;
  const canProceed = totalLineItems > 0 && !documentsBusy;

  // Custom-mode (subcontractor) tenants work in a single anchor division.
  // Auto-create one default "Line Items" category and hide the add-category UI.
  const tenantPrefs = useTenantPreferences();
  const isCustomMode = tenantPrefs.defaultVocabularyMode === 'custom';
  const anchorDivision = tenantPrefs.subcontractorAnchorDivision || null;

  const hasNoCategories = (estimate.categories || []).length === 0;
  useEffect(() => {
    if (!isCustomMode || !anchorDivision || !hasNoCategories) return;
    onEstimateChange({
      ...estimate,
      categories: [{
        categoryId: generateTempId(),
        categoryCode: anchorDivision,
        categoryName: 'Line Items',
        divisionNumber: parseInt(anchorDivision, 10) || null,
        subtotal: 0,
        lineItems: [],
      }],
    });
    // We intentionally exclude `estimate` and `onEstimateChange` from deps:
    // this effect should fire only when the "empty-categories + custom-mode"
    // condition first becomes true. Including the unstable parent props
    // would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomMode, anchorDivision, hasNoCategories]);

  const handleAddCategory = () => {
    const newCategory = {
      categoryId: generateTempId(),
      categoryName: 'New Category',
      categoryCode: '',
      subtotal: 0,
      lineItems: [],
    };
    onEstimateChange({
      ...estimate,
      categories: [...(estimate.categories || []), newCategory],
    });
  };

  const handleUpdateCategory = (updatedCategory) => {
    onEstimateChange({
      ...estimate,
      categories: estimate.categories.map(cat =>
        cat.categoryId === updatedCategory.categoryId ? updatedCategory : cat
      ),
    });
  };

  const handleDeleteCategory = (categoryId) => {
    onEstimateChange({
      ...estimate,
      categories: estimate.categories.filter(cat => cat.categoryId !== categoryId),
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Customize Your Template</CardTitle>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Add or remove categories and line items before AI estimation.
              {totalLineItems === 0 && (
                <span className="text-red-600 dark:text-red-400 ml-2 font-medium">
                  Add at least one line item to continue.
                </span>
              )}
            </p>
            {documentsBusy && (
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-2 font-medium">
                Waiting for {documentsUploadingCount > 0
                  ? `${documentsUploadingCount} upload${documentsUploadingCount !== 1 ? 's' : ''}`
                  : `${documentsProcessingCount} document${documentsProcessingCount !== 1 ? 's' : ''} to finish processing`}
                {' '}before AI Estimation can begin.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onBack}>
              Back
            </Button>
            {onSkipAI && (
              <Button variant="outline" onClick={onSkipAI} disabled={totalLineItems === 0}>
                Skip AI and Enter Manually
              </Button>
            )}
            <Button onClick={onProceed} disabled={!canProceed}>
              Proceed to AI Estimation
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {(estimate.categories || []).length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p className="mb-4">
              {isCustomMode
                ? 'Setting up your estimate...'
                : 'No categories yet. Add a category to get started.'}
            </p>
          </div>
        ) : (
          (estimate.categories || []).map(category => (
            <SimpleCategoryEditor
              key={category.categoryId}
              category={category}
              onUpdate={handleUpdateCategory}
              onDelete={() => handleDeleteCategory(category.categoryId)}
            />
          ))
        )}

        {!isCustomMode && (
          <Button variant="outline" onClick={handleAddCategory} className="mt-4">
            + Add Category
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function NewEstimateWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(STEPS.DESCRIBE);

  // Step 1: Project Description
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  // Auto-set so downstream code that consumes startDate still works without the
  // user having to fill it in. Mutable later via EstimateTreeEditor.
  const [startDate] = useState(todayISO());
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Draft estimate created when leaving step 1. Holds the row that uploads,
  // similarity search, document processing, and AI estimation all run against.
  const [estimateId, setEstimateId] = useState(null);

  // Step 2: Upload Documents and Photos — document panel reports its status here.
  const [documentStatus, setDocumentStatus] = useState({
    total: 0, processed: 0, processing: 0, failed: 0, uploading: 0,
  });
  // Similarity search runs in the background concurrently with document upload.
  const similaritySearchPromiseRef = useRef(null);
  const [isResolvingTemplate, setIsResolvingTemplate] = useState(false);

  // Step 4/5: AI Estimation
  const [templateEstimate, setTemplateEstimate] = useState(null);
  const [estimationProgress, setEstimationProgress] = useState({ total: 0, completed: 0, currentCategory: null, percentComplete: 0 });
  const [isEstimating, setIsEstimating] = useState(false);
  const [skipEstimation, setSkipEstimation] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Store original template for skip functionality
  const partialResultsRef = useRef([]); // Ref for accessing current partial results in skip handler (avoids stale closure)
  const [categoryProgress, setCategoryProgress] = useState({ total: 0, completed: 0 }); // Track category completion for smooth animation

  // Smooth progress animation (updates 5x per second)
  const displayProgress = useAnimatedProgress(
    estimationProgress.percentComplete,
    categoryProgress.total,
    categoryProgress.completed,
    { enabled: currentStep === STEPS.AI_ESTIMATING && isEstimating }
  );

  async function handleContinueFromDescribe() {
    setIsCreatingDraft(true);
    setSearchError(null);

    try {
      // Create the draft estimate up front so that document uploads, processing,
      // and AI estimation can all run against a real estimateId during the wizard.
      const draft = await estimatesApi.createEstimate({
        projectName,
        scopeDescription: description,
        description,
        location: location || null,
        startDate,
        status: 'draft',
        totalCost: 0,
        categories: [],
      });
      const newEstimateId = draft.estimateId;
      setEstimateId(newEstimateId);

      // Kick off the similarity search in the background. The user can keep
      // uploading documents on step 2 while this resolves; we'll await it when
      // they click Continue.
      similaritySearchPromiseRef.current = estimatesApi.searchSimilarEstimates({
        description,
        squareFootage: null,
        location: location || null,
      }).catch((err) => {
        console.error('Background similarity search failed:', err);
        return { results: [] };
      });

      setCurrentStep(STEPS.UPLOAD_DOCS);
    } catch (err) {
      console.error('Failed to create draft estimate:', err);
      setSearchError(err.message || 'Failed to create estimate');
    } finally {
      setIsCreatingDraft(false);
    }
  }

  async function handleContinueFromUpload() {
    setIsResolvingTemplate(true);
    try {
      // Wait for the in-flight similarity search (with a safety timeout) before
      // silently picking the highest-scoring template.
      const searchPromise = similaritySearchPromiseRef.current || Promise.resolve({ results: [] });
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => resolve({ results: [] }), 30000),
      );
      const response = await Promise.race([searchPromise, timeoutPromise]);
      const results = response.results || [];

      if (results.length > 0) {
        const best = results[0];
        await loadAndApplyTemplate(best.estimateId);
      } else {
        // No matches — fall back to an empty template like Create From Scratch.
        seedEmptyTemplate();
      }
    } catch (err) {
      console.error('Failed to resolve template:', err);
      // Graceful degradation: still let the user proceed with an empty template.
      seedEmptyTemplate();
    } finally {
      setIsResolvingTemplate(false);
    }
  }

  async function loadAndApplyTemplate(templateSourceId) {
    setSkipEstimation(false);
    partialResultsRef.current = [];

    try {
      const template = await estimatesApi.getEstimateById(templateSourceId);
      setSelectedTemplate(template);

      // Build estimate with user's project info + template structure.
      // estimateId is the draft we created on step 1 — preserve it so the
      // EstimateTreeEditor knows this is an existing row to update.
      const newEstimate = {
        estimateId,
        projectName,
        clientName: '',
        squareFootage: template.squareFootage,
        location: location || template.location,
        numberOfBuildings: template.numberOfBuildings,
        startDate,
        projectTimelineMonths: null,
        jobType: template.jobType,
        constructionType: template.constructionType,
        stories: template.stories,
        prevailingWage: template.prevailingWage,
        categories: (template.categories || []).map((cat) => ({
          ...cat,
          categoryId: generateTempId(),
          lineItems: (cat.lineItems || []).map((item) => ({
            ...item,
            lineItemId: generateTempId(),
            totalCost: item.totalCost,
            unitCost: item.unitCost,
            costSource: 'template',
          })),
        })),
      };

      setTemplateEstimate(newEstimate);
      setCurrentStep(STEPS.EDIT_TEMPLATE);
    } catch (err) {
      console.error('Failed to load template:', err);
      // Fall back to empty template so the user isn't stuck.
      seedEmptyTemplate();
    }
  }

  function seedEmptyTemplate() {
    const emptyEstimate = {
      estimateId,
      projectName,
      clientName: '',
      squareFootage: 0,
      location: location || '',
      numberOfBuildings: null,
      startDate,
      projectTimelineMonths: null,
      jobType: '',
      constructionType: '',
      stories: 1,
      prevailingWage: false,
      categories: [],
    };
    setTemplateEstimate(emptyEstimate);
    setSelectedTemplate(emptyEstimate);
    setCurrentStep(STEPS.EDIT_TEMPLATE);
  }

  async function runBatchEstimation(estimate, excludeEstimateId) {
    setIsEstimating(true);
    partialResultsRef.current = []; // Reset the ref
    setCategoryProgress({ total: estimate.categories.length, completed: 0 }); // Initialize category tracking for smooth animation

    try {
      // Prepare categories with their line items for batch processing
      // Each category is processed as a single batch request for efficiency
      const categoriesForBatch = estimate.categories.map(cat => ({
        categoryId: cat.categoryId,
        categoryName: cat.categoryName,
        lineItems: cat.lineItems.map(item => ({
          description: item.description,
          masterformatCode: item.masterformatCode,
          quantity: item.quantity,
          unit: item.unit,
        })),
      }));

      const projectContext = {
        jobType: estimate.jobType,
        squareFootage: estimate.squareFootage,
        location: estimate.location,
        prevailingWage: estimate.prevailingWage,
        startDate: estimate.startDate,
        projectTimelineMonths: estimate.projectTimelineMonths,
      };

      // Batch estimate by category with progress updates
      // Also track partial results for skip functionality
      const results = await estimatesApi.estimateLineItemsBatch(
        projectContext,
        categoriesForBatch,
        (progress) => setEstimationProgress(progress),
        excludeEstimateId,
        (partial, completedCategoryCount) => {
          partialResultsRef.current = partial; // Update ref immediately (for skip handler)
          // Update category progress for smooth animation
          if (completedCategoryCount !== undefined) {
            setCategoryProgress(prev => ({ ...prev, completed: completedCategoryCount }));
          }
        },
        // Pass the draft estimateId so the backend can pull in
        // document/photo facts for AI context.
        estimateId,
      );

      // Check if user skipped during estimation
      if (skipEstimation) {
        return; // handleSkipEstimation already applied partial results
      }

      // Apply AI estimates to the estimate structure (immutably)
      const updatedEstimate = applyAIEstimates(estimate, results);
      setTemplateEstimate(updatedEstimate);
      setCurrentStep(STEPS.EDIT);

    } catch (err) {
      console.error('Batch estimation failed:', err);
      // On failure, proceed to edit with template costs
      setCurrentStep(STEPS.EDIT);
    } finally {
      setIsEstimating(false);
    }
  }

  function applyAIEstimates(estimate, results) {
    // Create a map of results by description for matching
    const resultsByDescription = {};
    results.forEach(result => {
      const key = `${result.categoryName}:${result.description}`;
      resultsByDescription[key] = result;
    });

    // Apply estimates to line items (immutably - return new objects)
    const updatedCategories = estimate.categories.map(cat => {
      const updatedLineItems = cat.lineItems.map(item => {
        const key = `${cat.categoryName}:${item.description}`;
        const aiResult = resultsByDescription[key];

        if (aiResult && aiResult.estimationMethod !== 'failed') {
          // Compute total from unitCost × quantity
          const quantity = aiResult.quantity || item.quantity || 1;
          const unitCost = aiResult.unitCost || 0;
          const totalCost = quantity * unitCost;

          return {
            ...item,
            totalCost,
            unitCost,
            quantity,
            unit: aiResult.unit || item.unit,
            costSource: 'ai',
            aiEstimatedCost: totalCost,
            aiExplanation: aiResult.explanation,
            aiConfidenceScore: aiResult.confidenceScore,
            aiSimilarItems: aiResult.similarItems?.map(si => si.lineItemId) || [],
            aiPriceRange: aiResult.priceRange,
          };
        }
        // If AI failed, item keeps its template costSource and values
        return item;
      });

      const subtotal = updatedLineItems.reduce((sum, item) => sum + (item.totalCost || 0), 0);
      return {
        ...cat,
        lineItems: updatedLineItems,
        subtotal,
      };
    });

    // Recalculate total cost
    const totalCost = updatedCategories.reduce((sum, cat) => sum + (cat.subtotal || 0), 0);
    const costPerSf = estimate.squareFootage > 0 ? totalCost / estimate.squareFootage : 0;

    return {
      ...estimate,
      categories: updatedCategories,
      totalCost,
      costPerSf,
    };
  }

  function handleSkipEstimation() {
    setSkipEstimation(true);

    // Use ref to get current partial results (avoids stale closure from async state updates)
    const currentPartialResults = partialResultsRef.current;

    // Apply any partial AI results that have already been collected
    if (currentPartialResults.length > 0 && templateEstimate) {
      const updatedEstimate = applyAIEstimates(templateEstimate, currentPartialResults);
      setTemplateEstimate(updatedEstimate);
    }

    // Proceed to edit (items without AI estimates keep their template costs)
    setCurrentStep(STEPS.EDIT);
  }

  function handleProceedToAIEstimation() {
    // Get the template source estimate ID for exclusion (if we selected from a template)
    const excludeEstimateId = selectedTemplate?.estimateId || null;

    setCurrentStep(STEPS.AI_ESTIMATING);

    // Start batch AI estimation with the current template
    runBatchEstimation(templateEstimate, excludeEstimateId);
  }

  function handleBack() {
    if (currentStep === STEPS.UPLOAD_DOCS) {
      setCurrentStep(STEPS.DESCRIBE);
      setSearchError(null);
    } else if (currentStep === STEPS.EDIT_TEMPLATE) {
      setCurrentStep(STEPS.UPLOAD_DOCS);
      setTemplateEstimate(null);
      setSelectedTemplate(null);
    } else if (currentStep === STEPS.AI_ESTIMATING) {
      setSkipEstimation(true);
      setCurrentStep(STEPS.EDIT_TEMPLATE);
    } else if (currentStep === STEPS.EDIT) {
      setCurrentStep(STEPS.EDIT_TEMPLATE);
    }
  }

  function handleCancel() {
    navigate('/estimates');
  }

  async function handleSaveEstimate(finalEstimate) {
    try {
      const payload = { ...finalEstimate, status: 'draft' };
      const targetId = finalEstimate.estimateId || estimateId;
      if (targetId) {
        // Update the draft we created on step 1.
        await estimatesApi.updateEstimate(targetId, payload);
        navigate(`/estimates/${targetId}`);
      } else {
        const saved = await estimatesApi.createEstimate(payload);
        navigate(`/estimates/${saved.estimateId}`);
      }
    } catch (err) {
      console.error('Failed to save estimate:', err);
      throw err;
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Cancel
          </Button>
        </div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Create New Estimate</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          {currentStep === STEPS.DESCRIBE && 'Give your project a title and description'}
          {currentStep === STEPS.UPLOAD_DOCS && 'Upload documents and photos for this project'}
          {currentStep === STEPS.EDIT_TEMPLATE && 'Customize your template before AI estimation'}
          {currentStep === STEPS.AI_ESTIMATING && 'AI is generating cost estimates based on similar projects and your documents'}
          {currentStep === STEPS.EDIT && 'Edit and customize your estimate'}
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center mb-6 sm:mb-8 px-2 overflow-x-auto">
        <div className="flex items-center gap-2">
          {[
            { id: STEPS.DESCRIBE, label: 'Describe' },
            { id: STEPS.UPLOAD_DOCS, label: 'Upload' },
            { id: STEPS.EDIT_TEMPLATE, label: 'Customize' },
            { id: STEPS.AI_ESTIMATING, label: 'Estimate' },
            { id: STEPS.EDIT, label: 'Edit' },
          ].map((step, idx, arr) => {
            const order = arr.map((s) => s.id);
            const currentIdx = order.indexOf(currentStep);
            const stepIdx = order.indexOf(step.id);
            const isActive = stepIdx === currentIdx;
            const isDone = stepIdx < currentIdx;
            return (
              <React.Fragment key={step.id}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${
                  isActive ? 'bg-blue-600 text-white' :
                  isDone ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' :
                  'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>
                  {idx + 1}
                </div>
                <span className="hidden sm:inline text-sm font-medium">{step.label}</span>
                {idx < arr.length - 1 && <div className="w-4 sm:w-8 h-0.5 bg-gray-300 dark:bg-gray-700 mx-1 flex-shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step 1: Describe Project */}
      {currentStep === STEPS.DESCRIBE && (
        <Card>
          <CardHeader>
            <CardTitle>Describe Your Project</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Project Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g., Whole Foods Market - Denver"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Project Description <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Describe your project: scope, special requirements, anything the AI should know.
                </p>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Example: Tenant improvement for a 12,000 SF retail shell — new partition walls, HVAC modifications, and finishes throughout."
                  rows={6}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Location (optional)
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., Denver, CO"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {searchError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
                  {searchError}
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={handleContinueFromDescribe}
                  disabled={!projectName || !description || isCreatingDraft}
                >
                  {isCreatingDraft ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Setting up…
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Upload Documents and Photos */}
      {currentStep === STEPS.UPLOAD_DOCS && estimateId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Upload Documents and Photos</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Upload plans, specs, photos, or anything else for this project. They'll be analyzed in the background while you continue.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={handleBack} disabled={isResolvingTemplate}>
                  Back
                </Button>
                <Button
                  onClick={handleContinueFromUpload}
                  disabled={isResolvingTemplate || documentStatus.uploading > 0}
                >
                  {isResolvingTemplate ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Picking the best template…
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <EstimateDocumentsPanel
              estimateId={estimateId}
              editable
              title="Documents and Photos"
              onProcessingStatusChange={setDocumentStatus}
            />
          </CardContent>
        </Card>
      )}

      {/* Step 3: Edit Template (with processing gate) */}
      {currentStep === STEPS.EDIT_TEMPLATE && templateEstimate && (
        <TemplateEditor
          estimate={templateEstimate}
          onEstimateChange={setTemplateEstimate}
          onProceed={handleProceedToAIEstimation}
          onBack={handleBack}
          onSkipAI={() => {
            // Skip AI estimation entirely — go straight to the manual editor
            // with template costs preserved.
            setSkipEstimation(true);
            setCurrentStep(STEPS.EDIT);
          }}
          documentsProcessingCount={documentStatus.processing}
          documentsUploadingCount={documentStatus.uploading}
        />
      )}

      {/* Step 4: AI Estimation */}
      {currentStep === STEPS.AI_ESTIMATING && (
        <AIEstimationProgress
          progress={estimationProgress}
          displayProgress={displayProgress}
          onSkip={handleSkipEstimation}
        />
      )}

      {/* Step 5: Edit Estimate */}
      {currentStep === STEPS.EDIT && templateEstimate && (
        <EstimateTreeEditor
          initialEstimate={templateEstimate}
          projectName={projectName}
          onSave={handleSaveEstimate}
          onCancel={handleBack}
        />
      )}
    </div>
  );
}

export default NewEstimateWizard;
